import { CANVAS_LABEL, ELEMENT_TYPE } from '@/constants';
import type { ElementAttrs, ElementData, ElementLoadOptions } from '@/types/canvas-type';
import { Container, Graphics, Sprite, Text } from 'pixi.js';

export class Element extends Container {
  protected _data: ElementData;
  protected _playing: boolean = false;
  protected _isMotion: boolean = false;
  protected _isEditing: boolean = false;

  protected container: Container;
  protected targetContainer: Container;
  protected motionContainer: Container;
  protected elementMask: Graphics | null = null;

  constructor(data?: ElementData) {
    super();

    if (data) {
      this._data = data;
    } else {
      this._data = {
        id: 'element',
        type: ELEMENT_TYPE.DEFAULT
      };
    }

    this.setDefaultAttrs();

    this.label = CANVAS_LABEL.ELEMENT;
    this.container = new Container({ label: 'element-container' });
    this.targetContainer = new Container({ label: 'target-container' });
    this.motionContainer = new Container({ label: 'motion-container' });

    this.addChild(this.container);
    this.container.addChild(this.targetContainer);
    this.container.addChild(this.motionContainer);

    this.targetContainer.visible = !this._isMotion;
    this.motionContainer.visible = this._isMotion;
  }

  /**
   * 设置默认的attrs
   */
  protected setDefaultAttrs() {
    if (!this._data.attrs) this._data.attrs = {};

    const attrs = this._data.attrs;

    if (!attrs.zIndex) attrs.zIndex = 0;
    if (!attrs.x) attrs.x = 0;
    if (!attrs.y) attrs.y = 0;
    if (!attrs.width) attrs.width = 100;
    if (!attrs.height) attrs.height = 100;
    if (!attrs.originWidth) attrs.originWidth = 300;
    if (!attrs.originHeight) attrs.originHeight = 300;
    if (!attrs.rotation) attrs.rotation = 0;
    if (!attrs.alpha) attrs.alpha = 1;
    if (!attrs.scaleX) attrs.scaleX = 1;
    if (!attrs.scaleY) attrs.scaleY = 1;
  }

  public async load(_options?: ElementLoadOptions): Promise<boolean> {
    return true;
  }

  /**
   * 重载，用于替换素材链接等
   */
  public async reload(): Promise<boolean> {
    return true;
  }

  /**
   * 卸载已加载的内容
   */
  public unload() {}

  public async preload() {
    console.log(this._data.content, 'preload');
    return true;
  }

  public async clearPreload() {
    console.log(this._data.content, 'clearPreload');
  }

  protected setTarget(target: Sprite | Graphics | Text) {
    this.targetContainer.addChild(target);
  }

  public updateEditing() {}

  public initMask() {
    if (!this.elementMask) {
      this.elementMask = new Graphics();
      this.container.addChild(this.elementMask);
      this.container.mask = this.elementMask;
    }

    this.elementMask.visible = true;
    this.updateMask();
  }

  public removeMask() {
    if (this.container.mask) {
      this.container.mask = null;
    }
    if (this.elementMask) this.elementMask.visible = false;
  }

  public seek(_time: number) {
    // console.log(time);
  }

  public seekPlay(_time: number) {
    this._playing = true;
  }

  public pause() {
    this._playing = false;
  }

  public stop(): void {
    this.pause();
  }

  public updateMask() {
    if (!this.elementMask || !this.elementMask.visible) return;
    this.elementMask.clear();
    this.elementMask
      .rect(-this.originWidth / 2, -this.originHeight / 2, this.originWidth, this.originHeight)
      .fill(0xffffff);
  }

  /**
   * 从当前 view 的物理状态（scale/position）反向更新 attrs 数据
   * 通常在变形工具拖拽过程中或结束时调用
   */
  public syncAttrsFromView() {
    if (!this.attrs) return;

    // 1. 核心公式：逻辑宽度 = 原始宽度 * 当前缩放比
    // 这样即便变形工具只动了 scale，你的 width/height 也能实时更新
    this.attrs.width = Math.round(this.originWidth * this.scale.x);
    this.attrs.height = Math.round(this.originHeight * this.scale.y);

    // 2. 位置同步
    this.attrs.x = Math.round(this.x);
    this.attrs.y = Math.round(this.y);

    this.attrs.scaleX = this.scale.x;
    this.attrs.scaleY = this.scale.y;

    // 3. 如果有旋转
    this.attrs.rotation = Math.round((this.rotation * 180) / Math.PI);

    this.attrs.alpha = this.alpha;

    // 4. 同步 layout 数据，防止 applyParentLayout 用旧值还原位置
    this.syncLayoutFromView();
  }

  /**
   * 根据当前 view 的物理位置，反向更新 layout 中的 left/top/right/bottom 百分比值
   * 确保拖拽/缩放后 layout 数据与视图一致，避免 applyParentLayout 还原旧位置
   */
  public syncLayoutFromView() {
    const attrs = this.attrs;
    if (!attrs) return;

    const layout = attrs.layout;
    if (!layout) return;

    const parentWidth = layout.parentWidth ?? 0;
    const parentHeight = layout.parentHeight ?? 0;
    const elementWidth = attrs.width ?? this.originWidth;
    const elementHeight = attrs.height ?? this.originHeight;

    const x = attrs.x ?? 0;
    const y = attrs.y ?? 0;

    // 反向公式（由 applyParentLayout 的公式推导）：
    // left:   x = -pw/2 + left * pw + ew/2  =>  left = (x + pw/2 - ew/2) / pw
    // right:  x = pw/2 - right * pw - ew/2  =>  right = (pw/2 - x - ew/2) / pw
    // top:    y = -ph/2 + top * ph + eh/2   =>  top = (y + ph/2 - eh/2) / ph
    // bottom: y = ph/2 - bottom * ph - eh/2 =>  bottom = (ph/2 - y - eh/2) / ph
    if (parentWidth > 0) {
      if (layout.left !== undefined) {
        layout.left = (x + parentWidth / 2 - elementWidth / 2) / parentWidth;
      } else if (layout.right !== undefined) {
        layout.right = (parentWidth / 2 - x - elementWidth / 2) / parentWidth;
      } else {
        layout.left = (x + parentWidth / 2 - elementWidth / 2) / parentWidth;
      }

      // 同步直接百分比值（子类如 ImageElement 会用到）
      layout.x = (x + parentWidth / 2) / parentWidth;
      layout.width = elementWidth / parentWidth;
    }

    if (parentHeight > 0) {
      if (layout.top !== undefined) {
        layout.top = (y + parentHeight / 2 - elementHeight / 2) / parentHeight;
      } else if (layout.bottom !== undefined) {
        layout.bottom = (parentHeight / 2 - y - elementHeight / 2) / parentHeight;
      } else {
        layout.top = (y + parentHeight / 2 - elementHeight / 2) / parentHeight;
      }

      // 同步直接百分比值（子类如 ImageElement 会用到）
      layout.y = (y + parentHeight / 2) / parentHeight;
      layout.height = elementHeight / parentHeight;
    }
  }

  /**
   * 应用属性数据到view
   * @returns
   */
  public applyAttrs() {
    if (!this.attrs) return;

    this.setSize(this.attrs.width ?? 100, this.attrs.height ?? 100);

    this.applyParentLayout();

    this.position.set(this.attrs.x ?? 0, this.attrs.y ?? 0);

    this.rotation = ((this.attrs.rotation ?? 0) * Math.PI) / 180;

    this.alpha = this.attrs.alpha ?? 1;
  }

  /**
   * 根据父容器的尺寸和 layout 配置（left/top/right/bottom）计算元素的 x/y 位置
   * 仅当 layout 中有对应值时才会计算
   */
  public applyParentLayout() {
    const attrs = this.attrs;
    if (!attrs) return;

    const layout = attrs.layout;
    if (!layout) return;

    const ds = layout.displayScale ?? 1;
    const parentWidth = layout.parentWidth ?? 0;
    const parentHeight = layout.parentHeight ?? 0;
    const elementWidth = (attrs.width ?? this.originWidth) * ds;
    const elementHeight = (attrs.height ?? this.originHeight) * ds;

    // 计算 x：left 优先，其次 right（右对齐），都没有则用 originX 等比缩放
    // layout 中的值是百分比（0-1），父容器和元素都以 (0,0) 为中心点
    if (layout.left !== undefined && parentWidth !== undefined) {
      attrs.x = -parentWidth / 2 + layout.left * parentWidth + elementWidth / 2;
    } else if (layout.right !== undefined && parentWidth !== undefined) {
      attrs.x = parentWidth / 2 - layout.right * parentWidth - elementWidth / 2;
      // 一次性转换为 left，后续统一走 left 分支
      layout.left = 1 - layout.right - elementWidth / parentWidth;
      delete layout.right;
    } else if (attrs.originX !== undefined) {
      // 无 layout 定位：用 originX（设计稿坐标不变量）× displayScale 等比缩放
      attrs.x = attrs.originX * ds;
    }

    // 计算 y：top 优先，其次 bottom（底部对齐），都没有则用 originY 等比缩放
    if (layout.top !== undefined && parentHeight !== undefined) {
      attrs.y = -parentHeight / 2 + layout.top * parentHeight + elementHeight / 2;
    } else if (layout.bottom !== undefined && parentHeight !== undefined) {
      attrs.y = parentHeight / 2 - layout.bottom * parentHeight - elementHeight / 2;
      // 一次性转换为 top，后续统一走 top 分支
      layout.top = 1 - layout.bottom - elementHeight / parentHeight;
      delete layout.bottom;
    } else if (attrs.originY !== undefined) {
      // 无 layout 定位：用 originY（设计稿坐标不变量）× displayScale 等比缩放
      attrs.y = attrs.originY * ds;
    }
  }

  /**
   * 根据数据修改view，这个方法不修改数据中的attrs，主要用于动画过程中修改view的属性
   * @param data
   * @returns
   */
  public updateAttrs(data: ElementAttrs) {
    if (!data) return;

    if (data.width !== undefined) this.setSize(this.attrs.width ?? 100, this.attrs.height ?? 100);

    if (data.x !== undefined) this.position.set(this.attrs.x ?? 0, this.attrs.y ?? 0);

    if (data.rotation !== undefined) this.rotation = ((this.attrs.rotation ?? 0) * Math.PI) / 180;

    if (data.alpha !== undefined) this.alpha = this.attrs.alpha ?? 1;

    if (data.scaleX !== undefined && data.scaleY !== undefined) {
      this.scale.set(data.scaleX, data.scaleY);
    }
  }

  /**
   * 刷新，用于更新素材属性等
   */
  public refresh() {
    this.applyAttrs();
  }

  public setSize(width: number, height: number) {
    if (!this.attrs) return;
    //都做一下相同值判断，防止触发二次watch更新
    if (this.attrs.width !== width) this.attrs.width = width;
    if (this.attrs.height !== height) this.attrs.height = height;

    // 通过 目标值 / 原始值 算出缩放比
    const ds = this.attrs.layout?.displayScale ?? 1;
    this.scale.set((width * ds) / this.originWidth, (height * ds) / this.originHeight);

    if (this.attrs.scaleX !== this.scale.x) this.attrs.scaleX = this.scale.x;
    if (this.attrs.scaleY !== this.scale.y) this.attrs.scaleY = this.scale.y;

    this.redraw();
  }

  protected redraw() {
    this.updateMask();
  }

  public show() {
    this.visible = true;
  }

  public hide() {
    this.visible = false;
  }

  public destroy() {
    if (this.elementMask) {
      this.elementMask.destroy({ children: true, texture: true, context: true });
      this.elementMask = null;
    }

    super.destroy({ children: true });
  }

  public get data(): ElementData {
    return this._data;
  }

  public set data(v: ElementData) {
    this._data = v;

    this.setDefaultAttrs();
  }

  public get id() {
    return this._data.id ?? '';
  }

  public get attrs(): ElementAttrs {
    if (!this._data.attrs) this.setDefaultAttrs();

    return this._data.attrs!;
  }

  public set attrs(v: ElementAttrs) {
    this._data.attrs = v;
  }

  public get elementWidth(): number {
    return this.attrs?.width ?? 0;
  }

  public get elementHeight(): number {
    return this.attrs?.height ?? 0;
  }

  public get originWidth(): number {
    return this.attrs?.originWidth ?? 100;
  }
  public get originHeight(): number {
    return this.attrs?.originHeight ?? 100;
  }

  public get zIndex(): number {
    return this.attrs.zIndex ?? 0;
  }

  public get playing(): boolean {
    return this._playing;
  }

  public get isEditing(): boolean {
    return this._isEditing;
  }

  public set isEditing(v: boolean) {
    this._isEditing = v;
  }
}
